<template>
  <div class="card p-shadow-app">
    <Toast />
    <ConfirmDialog />
    <Loading :active.sync="loading" :isFullPage="false" />

    <div class="p-mb-3 p-d-flex p-jc-between p-ai-center">
      <h1 class="p-m-0">Compras</h1>
      <Button
        v-if="can('seeAddEditPurchases')"
        label="Nova Compra"
        icon="pi pi-plus"
        class="p-button-primary"
        @click="$router.push({ name: 'PurchaseCreate' })"
      />
    </div>

    <!-- FILTERS -->
    <div class="p-fluid p-formgrid p-grid p-mb-3">
      <div class="p-field p-col-12 p-md-3">
        <label for="supplier">Fornecedor</label>
        <AppAutocomplete
          fieldId="supplier"
          v-model="filters.supplier_no"
          :optionLabel="getSupplierLabel"
          optionValue="id"
          :editModel="getCurrentSupplier"
          :fetchFunction="fetchSuppliers"
          placeholder="Todos os fornecedores"
          @clear-selection="clearSupplier"
          appendTo="body"
        >
          <template #option="slotProps">
            <div>
              <b>#{{ slotProps.option.id }} - {{ slotProps.option.name }}</b><br />
              <span v-if="slotProps.option.nif">NIF: {{ slotProps.option.nif }}</span>
            </div>
          </template>
        </AppAutocomplete>
      </div>

      <div class="p-field p-col-12 p-md-2">
        <label for="nfact">Nº Documento</label>
        <InputText
          id="nfact"
          v-model="filters.nfact"
          placeholder="Filtrar por nº..."
        />
      </div>

      <div class="p-field p-col-12 p-md-2">
        <label for="dateFrom">Data De</label>
        <Calendar
          id="dateFrom"
          v-model="filters.dateFrom"
          dateFormat="yy-mm-dd"
          placeholder="Data inicial"
          showClear
        />
      </div>

      <div class="p-field p-col-12 p-md-2">
        <label for="dateTo">Data Até</label>
        <Calendar
          id="dateTo"
          v-model="filters.dateTo"
          dateFormat="yy-mm-dd"
          placeholder="Data final"
          showClear
        />
      </div>

      <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end">
        <Button
          label="Aplicar Filtros"
          icon="pi pi-filter"
          class="p-button-info"
          @click="applyFilters"
        />
      </div>

      <div class="p-field p-col-12 p-md-1 p-d-flex p-ai-end">
        <Button
          label="Limpar"
          icon="pi pi-times"
          class="p-button-secondary p-button-text"
          @click="clearFilters"
        />
      </div>
    </div>

    <!-- DATATABLE -->
    <DataTable
      ref="purchasesTable"
      :value="purchasesList"
      :loading="loading"
      :paginator="true"
      :rows="perPage"
      :rowsPerPageOptions="[10, 15, 20, 50, 100]"
      responsiveLayout="scroll"
      class="p-datatable-sm"
      stripedRows
      :lazy="true"
      :totalRecords="totalRecords"
      @page="onPage"
      @sort="onSort"
      :expandedRows.sync="expandedRows"
    >
      <template #empty>
        <div class="p-text-center p-p-4">
          <i class="pi pi-inbox" style="font-size: 3rem; color: #aaa;"></i>
          <p class="p-mt-2 text-secondary">Nenhuma compra encontrada.</p>
        </div>
      </template>

      <Column :expander="true" headerStyle="width: 3rem" />

      <Column field="nmdoc" header="Documento" sortable style="min-width: 120px;">
        <template #body="slotProps">
          <span class="p-text-bold">{{ slotProps.data.nmdoc }}</span>
        </template>
      </Column>

      <Column field="nfact" header="Nº Documento" sortable style="min-width: 140px;">
        <template #body="slotProps">
          <span>#{{ slotProps.data.nfact }}</span>
        </template>
      </Column>

      <Column field="nome" header="Fornecedor" sortable style="min-width: 220px;">
        <template #body="slotProps">
          <span>{{ slotProps.data.nome }}</span>
        </template>
      </Column>

      <Column field="dataopen" header="Data Interna" sortable style="min-width: 120px;">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.dataopen) }}
        </template>
      </Column>

      <Column field="datadoc" header="Data Original" sortable style="min-width: 120px;">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.datadoc) }}
        </template>
      </Column>

      <Column field="vencimento" header="Vencimento" sortable style="min-width: 120px;">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.vencimento) }}
        </template>
      </Column>

      <Column field="total" header="Total" sortable class="text-right" style="min-width: 120px;">
        <template #body="slotProps">
          <span class="p-text-bold text-success">{{ formatCurrency(slotProps.data.total, slotProps.data.moeda) }}</span>
        </template>
      </Column>

      <Column field="status" header="Status" style="min-width: 140px;">
        <template #body="slotProps">
          <span class="p-tag p-tag-info" v-if="slotProps.data.status !== 'Regularizado'">
            {{ slotProps.data.status || 'Por Regularizar.' }}
          </span>
          <span class="p-tag p-tag-success" v-else>
            {{ slotProps.data.status }}
          </span>
        </template>
      </Column>

      <Column header="Ações" style="min-width: 120px;" class="text-center">
        <template #body="slotProps">
          <Button
            v-if="can('seeAddEditPurchases')"
            icon="pi pi-pencil"
            class="p-button-rounded p-button-warning p-button-sm p-mr-1"
            @click="editPurchase(slotProps.data.fostamp)"
            tooltip="Editar Compra"
          />
        </template>
      </Column>

      <!-- ROW EXPANSION: LINES PREVIEW -->
      <template #expansion="slotProps">
        <div class="p-p-3" style="background-color: #fafafa; border-radius: 4px; overflow-x: auto;">
          <p class="p-mb-2">Linhas do documento ({{ slotProps.data.lines ? slotProps.data.lines.length : 0 }})</p>
          <table class="p-datatable-table p-datatable-sm" style="background: white; border: 1px solid #e0e0e0; min-width: 750px;">
            <thead class="p-datatable-thead">
              <tr>
                <th>Referência</th>
                <th>Designação</th>
                <th class="text-right">Quant.</th>
                <th class="text-right">Preço</th>
                <th class="text-right">Desc. %</th>
                <th class="text-right">Taxa IVA</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody class="p-datatable-tbody">
              <tr v-for="(line, idx) in slotProps.data.lines" :key="idx">
                <td>{{ line.ref }}</td>
                <td>{{ line.design }}</td>
                <td class="text-right">{{ line.qtt }}</td>
                <td class="text-right">{{ formatCurrency(line.preco, slotProps.data.moeda) }}</td>
                <td class="text-right">{{ line.desconto || 0 }}%</td>
                <td class="text-right">{{ line.taxaiva ?? 23 }}%</td>
                <td class="text-right p-text-bold">{{ formatCurrency(line.total, slotProps.data.moeda) }}</td>
              </tr>
              <tr v-if="!slotProps.data.lines || slotProps.data.lines.length === 0">
                <td colspan="7" class="text-center p-p-2 text-secondary">Sem linhas associadas.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script>
import purchaseService from "@/services/purchase.service";
import supplierService from "@/services/supplier.service";
import AppAutocomplete from "@/components/elements/AppAutocomplete.vue";
import Loading from "vue-loading-overlay";

export default {
  name: "Purchases",
  components: { AppAutocomplete, Loading },
  data() {
    return {
      loading: false,
      purchasesList: [],
      totalRecords: 0,
      page: 1,
      perPage: 15,
      sortField: "dataopen",
      sortOrder: -1,
      expandedRows: [],
      filters: {
        supplier_no: null,
        nfact: "",
        dateFrom: null,
        dateTo: null,
      },
    };
  },
  activated() {
    this.fetchPurchases();
  },
  async created() {
    await this.fetchPurchases();
  },
  methods: {
    async fetchSuppliers(search = "") {
      return (await supplierService.query({ search })) || [];
    },

    async getCurrentSupplier(no) {
      if (!no) return null;
      const suppliers = await supplierService.query({ no });
      return suppliers && suppliers.length ? suppliers[0] : null;
    },

    getSupplierLabel(supplier) {
      if (!supplier) return "";
      return `#${supplier.id} - ${supplier.name} | NIF: ${supplier.nif || ""}`;
    },

    clearSupplier() {
      this.filters.supplier_no = null;
    },

    formatDateForApi(d) {
      if (!d) return null;
      if (typeof d === "string") return d;
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const dy = String(d.getDate()).padStart(2, "0");
      return `${yr}-${mo}-${dy}`;
    },

    async fetchPurchases() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
          sort_field: this.sortField,
          sort_order: this.sortOrder === 1 ? "asc" : "desc",
          supplier_no: this.filters.supplier_no,
          nfact: this.filters.nfact,
          date_from: this.formatDateForApi(this.filters.dateFrom),
          date_to: this.formatDateForApi(this.filters.dateTo),
        };

        const res = await purchaseService.getPurchases(params);
        if (res && res.data) {
          this.purchasesList = res.data;
          this.totalRecords = res.total || res.data.length;
        } else {
          this.purchasesList = [];
          this.totalRecords = 0;
        }
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível carregar a lista de compras.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },

    clearFilters() {
      this.filters = {
        supplier_no: null,
        nfact: "",
        dateFrom: null,
        dateTo: null,
      };
      this.page = 1;
      if (this.$refs.purchasesTable) this.$refs.purchasesTable.d_first = 0;
      this.fetchPurchases();
    },

    applyFilters() {
      this.page = 1;
      if (this.$refs.purchasesTable) this.$refs.purchasesTable.d_first = 0;
      this.fetchPurchases();
    },

    onPage(event) {
      this.page = event.page + 1;
      this.perPage = event.rows;
      this.fetchPurchases();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.page = 1;
      if (this.$refs.purchasesTable) this.$refs.purchasesTable.d_first = 0;
      this.fetchPurchases();
    },

    editPurchase(stamp) {
      this.$router.push({ name: "PurchaseEdit", params: { stamp } });
    },

    confirmDelete(row) {
      this.$confirm.require({
        message: `Tem a certeza que deseja eliminar a compra #${row.nfact}?`,
        header: "Confirmar Eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Eliminar",
        rejectLabel: "Cancelar",
        acceptClass: "p-button-danger",
        accept: async () => {
          this.loading = true;
          try {
            await purchaseService.deletePurchase(row.fostamp);
            this.$toast.add({
              severity: "success",
              summary: "Eliminado",
              detail: "Compra eliminada com sucesso.",
              life: 3000,
            });
            await this.fetchPurchases();
          } catch (err) {
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao eliminar compra.",
              life: 3000,
            });
          } finally {
            this.loading = false;
          }
        },
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return String(dateStr).slice(0, 10);
    },

    formatCurrency(val, purchaseCurrency = "EURO") {
      const currency = purchaseCurrency === "EURO" ? "EUR" : (purchaseCurrency || "EUR");
      return new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency,
      }).format(val || 0);
    },
  },
};
</script>
